import { useEffect, useRef } from 'react'
import { AppState, type AppStateStatus } from 'react-native'
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay'

import { type useBadgeSyncQuery } from '@/__generated__/useBadgeSyncQuery.graphql'
import { useNotifications } from '@/hooks/useNotifications'

const badgeSyncQuery = graphql`
  query useBadgeSyncQuery {
    currentUserUnreadCount
    # Also refresh the per-tab unviewed counts so UserNavigatorView repaints its tab badges.
    currentUser {
      unviewedLikesCount
      unviewedMatchMessagesCount
      unviewedSystemMessagesCount
    }
  }
`

// Single owner of the iOS app-icon badge. Applies the latest server-computed
// currentUserUnreadCount whenever it changes in the Relay store, and refetches
// it when the app returns to the foreground (covers pushes received while
// backgrounded, server-side changes, and reinstall/account-switch).
export const useBadgeSync = (currentUserUnreadCount: number | null | undefined) => {
  const { setBadgeCountAsync } = useNotifications()
  const environment = useRelayEnvironment()
  const appStateRef = useRef<AppStateStatus>(AppState.currentState)

  useEffect(() => {
    void setBadgeCountAsync(currentUserUnreadCount ?? 0)
  }, [currentUserUnreadCount, setBadgeCountAsync])

  useEffect(() => {
    let inFlight: { unsubscribe: () => void } | null = null

    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (/inactive|background/.test(appStateRef.current) && nextAppState === 'active') {
        // cancel any still-in-flight refetch before starting a new one
        inFlight?.unsubscribe()
        inFlight = fetchQuery<useBadgeSyncQuery>(
          environment,
          badgeSyncQuery,
          {},
          {
            fetchPolicy: 'network-only'
          }
        ).subscribe({
          // best-effort refresh; surface failures in dev rather than swallowing them
          error: (error: unknown) => {
            if (__DEV__) {
              console.warn('[useBadgeSync] foreground badge refetch failed', error)
            }
          }
        })
      }

      appStateRef.current = nextAppState
    })

    return () => {
      inFlight?.unsubscribe()
      subscription.remove()
    }
  }, [environment])
}
